import {
  Activity,
  ArrowLeft,
  ArrowUpRight,
  Bot,
  ChevronRight,
  CircleDollarSign,
  Droplets,
  ExternalLink,
  Gauge,
  Menu,
  Plus,
  Radio,
  RefreshCw,
  ShieldCheck,
  Wallet,
  X,
  Zap
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { keccak256, parseUnits, toBytes, type Address, type Hex } from "viem";
import {
  conditionalTokensAbi,
  erc20Abi,
  fpmmAbi,
  marketFactoryAbi,
  positionLensAbi
} from "./abis";
import { config, contractsConfigured, robinhoodTestnet } from "./config";
import { getHealth, getMarkets, getQuote, storeMetadata } from "./lib/api";
import {
  formatCollateral,
  formatDate,
  formatMetric,
  impliedProbability,
  shortenAddress
} from "./lib/format";
import { buildTradePlan, percentToScalar } from "./lib/transactions";
import { connectWallet, publicClient, type WalletSession } from "./lib/wallet";
import type { Health, Market, Position, Quote } from "./types";

type View = "markets" | "portfolio" | "create" | "docs";

const zeroBytes32 = `0x${"0".repeat(64)}` as Hex;

export default function App() {
  const [view, setView] = useState<View>("markets");
  const [markets, setMarkets] = useState<Market[]>([]);
  const [health, setHealth] = useState<Health | null>(null);
  const [selected, setSelected] = useState<Market | null>(null);
  const [wallet, setWallet] = useState<WalletSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextHealth, nextMarkets] = await Promise.all([getHealth(), getMarkets()]);
      setHealth(nextHealth);
      setMarkets(nextMarkets);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to load markets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const ensureWallet = useCallback(async () => {
    if (wallet) return wallet;
    const connected = await connectWallet();
    setWallet(connected);
    return connected;
  }, [wallet]);

  const navigate = (next: View) => {
    setSelected(null);
    setView(next);
    setMobileOpen(false);
  };

  const faucet = async () => {
    try {
      setNotice("Opening the tUSDG faucet…");
      const session = await ensureWallet();
      await session.write({
        address: config.contracts.collateral,
        abi: erc20Abi,
        functionName: "faucet"
      });
      setNotice("10,000 test tUSDG received.");
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : "Faucet transaction failed");
    }
  };

  return (
    <div className="app-shell">
      <div className="ticker" aria-label="Network status">
        <span><Radio size={12} /> ROBINHOOD TESTNET</span>
        <span>CHAIN 46630</span>
        <span>BLOCK {health?.latestBlock ?? "—"}</span>
        <span>FULLY COLLATERALIZED</span>
        <span>SHORT 00 / LONG 01</span>
      </div>

      <header className="topbar">
        <button className="wordmark" onClick={() => navigate("markets")} aria-label="Tension home">
          <span className="wordmark-mark">T//</span>
          <span>TENSION</span>
        </button>
        <nav className={mobileOpen ? "nav nav-open" : "nav"}>
          <NavButton active={view === "markets" && !selected} onClick={() => navigate("markets")}>Markets</NavButton>
          <NavButton active={view === "portfolio"} onClick={() => navigate("portfolio")}>Portfolio</NavButton>
          <NavButton active={view === "create"} onClick={() => navigate("create")}>Create</NavButton>
          <NavButton active={view === "docs"} onClick={() => navigate("docs")}>Docs</NavButton>
        </nav>
        <div className="topbar-actions">
          <button className="ghost-button faucet-button" onClick={() => void faucet()}>
            <CircleDollarSign size={16} /> Get tUSDG
          </button>
          <button className="wallet-button" onClick={() => void ensureWallet().catch((cause) => setNotice(String(cause)))}>
            <Wallet size={16} /> {wallet ? shortenAddress(wallet.account) : "Connect"}
          </button>
          <button className="menu-button" onClick={() => setMobileOpen((open) => !open)} aria-label="Toggle menu">
            {mobileOpen ? <X /> : <Menu />}
          </button>
        </div>
      </header>

      {notice && (
        <button className="notice" onClick={() => setNotice(null)}>
          <span>{notice}</span><X size={14} />
        </button>
      )}

      <main>
        {selected ? (
          <MarketDetail
            market={selected}
            wallet={wallet}
            ensureWallet={ensureWallet}
            onBack={() => setSelected(null)}
            setNotice={setNotice}
          />
        ) : view === "markets" ? (
          <MarketsView
            markets={markets}
            health={health}
            loading={loading}
            error={error}
            onRefresh={refresh}
            onSelect={setSelected}
            onCreate={() => navigate("create")}
          />
        ) : view === "portfolio" ? (
          <PortfolioView
            markets={markets}
            wallet={wallet}
            ensureWallet={ensureWallet}
            setNotice={setNotice}
          />
        ) : view === "create" ? (
          <CreateView
            wallet={wallet}
            ensureWallet={ensureWallet}
            setNotice={setNotice}
            onCreated={() => { navigate("markets"); void refresh(); }}
          />
        ) : (
          <DocsView />
        )}
      </main>

      <footer>
        <div><span className="wordmark-mark">T//</span> TENSION MARKETS</div>
        <p>Testnet software. tUSDG has no monetary value. No mainnet contracts are deployed.</p>
        <a href={robinhoodTestnet.blockExplorers.default.url} target="_blank" rel="noreferrer">
          Explorer <ArrowUpRight size={13} />
        </a>
      </footer>
    </div>
  );
}

function NavButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button className={active ? "nav-button active" : "nav-button"} onClick={onClick}>{children}</button>;
}

function MarketsView({
  markets,
  health,
  loading,
  error,
  onRefresh,
  onSelect,
  onCreate
}: {
  markets: Market[];
  health: Health | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => Promise<void>;
  onSelect: (market: Market) => void;
  onCreate: () => void;
}) {
  const openCount = markets.filter((market) => !market.resolved).length;
  const liquidity = markets.reduce((sum, market) => sum + BigInt(market.initialLiquidity), 0n);

  return (
    <>
      <section className="hero">
        <div className="hero-copy">
          <p className="eyebrow"><Zap size={14} /> SCALAR DERIVATIVES / 24×7</p>
          <h1><span className="sr-only">Trade the tension.</span><span aria-hidden="true">Trade the<br /><em>tension.</em></span></h1>
          <p className="hero-deck">
            Long or short the forces moving crypto markets—funding pressure and realized
            volatility—with bounded, fully collateralized positions.
          </p>
          <div className="hero-actions">
            <button className="signal-button" onClick={() => markets[0] && onSelect(markets[0])} disabled={!markets.length}>
              Explore live markets <ChevronRight size={17} />
            </button>
            <button className="text-button" onClick={onCreate}>Launch a market <Plus size={15} /></button>
          </div>
        </div>
        <div className="hero-instrument" aria-hidden="true">
          <div className="instrument-ring ring-one" />
          <div className="instrument-ring ring-two" />
          <div className="instrument-axis axis-x" />
          <div className="instrument-axis axis-y" />
          <div className="instrument-needle" />
          <span className="instrument-long">LONG</span>
          <span className="instrument-short">SHORT</span>
          <span className="instrument-value">σ / f</span>
        </div>
      </section>

      <section className="stat-rail">
        <Stat label="Open markets" value={String(openCount)} icon={<Activity />} />
        <Stat label="Seeded liquidity" value={`$${formatCollateral(liquidity)}`} icon={<Droplets />} />
        <Stat label="Onchain markets" value={health?.marketCount ?? "—"} icon={<Gauge />} />
        <Stat label="Settlement" value="SIGNED + FALLBACK" icon={<ShieldCheck />} compact />
      </section>

      <section className="market-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">LIVE SURFACE</p>
            <h2>Market board</h2>
          </div>
          <button className="refresh-button" onClick={() => void onRefresh()} aria-label="Refresh markets"><RefreshCw size={16} /></button>
        </div>
        {error && <div className="error-panel">{error}</div>}
        <div className="market-grid">
          {loading ? [0, 1, 2].map((item) => <div className="market-card skeleton" key={item} />) : null}
          {!loading && markets.map((market) => (
            <MarketCard market={market} key={market.id} onOpen={() => onSelect(market)} />
          ))}
          {!loading && !markets.length && (
            <div className="empty-state">
              <h3>No markets seeded yet.</h3>
              <p>Create the first bounded funding or volatility market.</p>
              <button className="signal-button" onClick={onCreate}>Define market <Plus size={16} /></button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function Stat({ label, value, icon, compact = false }: { label: string; value: string; icon: React.ReactNode; compact?: boolean }) {
  return <div className="stat"><span>{icon}{label}</span><strong className={compact ? "compact" : ""}>{value}</strong></div>;
}

function MarketCard({ market, onOpen }: { market: Market; onOpen: () => void }) {
  const probability = impliedProbability(market.longQuotePerCollateral, market.shortQuotePerCollateral);
  const status = market.resolved ? (market.invalid ? "REFUND" : "RESOLVED") : "LIVE";
  return (
    <button className="market-card" onClick={onOpen} aria-label={`Open market ${market.id}`}>
      <div className="card-topline">
        <span className={`metric-tag metric-${market.metric}`}>{market.metric === 0 ? "FUNDING" : "VOLATILITY"}</span>
        <span className={`status status-${status.toLowerCase()}`}><i />{status}</span>
      </div>
      <h3>{marketTitle(market)}</h3>
      <p className="range">RANGE {formatMetric(market.lowerBound, market.metric)} → {formatMetric(market.upperBound, market.metric)}</p>
      <div className="probability-row">
        <div>
          <span>LONG IMPLIED</span>
          <strong>{(probability * 100).toFixed(1)}%</strong>
        </div>
        <MiniChart probability={probability} metric={market.metric} />
      </div>
      <div className="card-meter"><span style={{ width: `${probability * 100}%` }} /></div>
      <div className="card-footer">
        <span>LIQ ${formatCollateral(market.initialLiquidity)}</span>
        <span>{(Number(market.fee) / 1e16).toFixed(1)}% FEE</span>
        <span>ENDS {formatDate(market.resolutionTime)}</span>
      </div>
    </button>
  );
}

function MiniChart({ probability, metric }: { probability: number; metric: number }) {
  const y = 35 - probability * 22;
  return (
    <svg viewBox="0 0 120 44" className={`mini-chart chart-${metric}`}>
      <path d={`M0 32 C18 28 24 36 40 25 S65 8 78 20 S100 ${y + 8} 120 ${y}`} fill="none" />
      <path d={`M0 32 C18 28 24 36 40 25 S65 8 78 20 S100 ${y + 8} 120 ${y} L120 44 L0 44Z`} />
    </svg>
  );
}

function MarketDetail({
  market,
  wallet,
  ensureWallet,
  onBack,
  setNotice
}: {
  market: Market;
  wallet: WalletSession | null;
  ensureWallet: () => Promise<WalletSession>;
  onBack: () => void;
  setNotice: (message: string | null) => void;
}) {
  const probability = impliedProbability(market.longQuotePerCollateral, market.shortQuotePerCollateral);
  return (
    <section className="detail-page">
      <button className="back-button" onClick={onBack}><ArrowLeft size={16} /> Back to markets</button>
      <div className="detail-head">
        <div>
          <div className="card-topline">
            <span className={`metric-tag metric-${market.metric}`}>{market.metric === 0 ? "FUNDING RATE" : "REALIZED VOL"}</span>
            <span className="status status-live"><i />{market.resolved ? "RESOLVED" : "LIVE"}</span>
          </div>
          <h1>{marketTitle(market)}</h1>
          <p>Linear payout between {formatMetric(market.lowerBound, market.metric)} and {formatMetric(market.upperBound, market.metric)}. SHORT is outcome 0; LONG is outcome 1.</p>
        </div>
        <div className="big-probability"><span>LONG IMPLIED</span><strong>{(probability * 100).toFixed(1)}%</strong></div>
      </div>
      <div className="detail-grid">
        <div className="detail-main">
          <div className="range-visual">
            <div className="range-labels"><span>SHORT FLOOR<br /><b>{formatMetric(market.lowerBound, market.metric)}</b></span><span>LONG CEILING<br /><b>{formatMetric(market.upperBound, market.metric)}</b></span></div>
            <div className="range-track"><span className="range-fill" style={{ width: `${probability * 100}%` }} /><i style={{ left: `${probability * 100}%` }} /></div>
            <div className="range-outcomes"><span>00 / SHORT</span><span>01 / LONG</span></div>
          </div>
          <div className="market-facts">
            <Fact label="Resolution" value={formatDate(market.resolutionTime)} />
            <Fact label="Report deadline" value={formatDate(market.reportDeadline)} />
            <Fact label="Seed liquidity" value={`$${formatCollateral(market.initialLiquidity)}`} />
            <Fact label="LP fee" value={`${(Number(market.fee) / 1e16).toFixed(1)}%`} />
            <Fact label="Condition" value={shortenAddress(market.conditionId)} />
            <Fact label="Pool" value={shortenAddress(market.fpmm)} />
          </div>
          <LiquidityPanel market={market} wallet={wallet} ensureWallet={ensureWallet} setNotice={setNotice} />
        </div>
        <TradePanel market={market} wallet={wallet} ensureWallet={ensureWallet} setNotice={setNotice} />
      </div>
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div><span>{label}</span><strong>{value}</strong></div>;
}

function TradePanel({ market, wallet, ensureWallet, setNotice }: {
  market: Market;
  wallet: WalletSession | null;
  ensureWallet: () => Promise<WalletSession>;
  setNotice: (message: string | null) => void;
}) {
  const [side, setSide] = useState<"SHORT" | "LONG">("LONG");
  const [direction, setDirection] = useState<"BUY" | "SELL">("BUY");
  const [amount, setAmount] = useState("100");
  const [quote, setQuote] = useState<Quote | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const collateralAmount = safeParseCollateral(amount);
    if (collateralAmount <= 0n) { setQuote(null); return; }
    const timer = window.setTimeout(() => {
      void getQuote(market.id, side, direction, collateralAmount)
        .then(setQuote)
        .catch(() => setQuote(null));
    }, 180);
    return () => window.clearTimeout(timer);
  }, [market.id, side, direction, amount]);

  const submit = async () => {
    if (!quote) { setNotice("A current quote is required."); return; }
    try {
      setBusy(true);
      const session = await ensureWallet();
      const collateralAmount = BigInt(quote.collateralAmount);
      const plan = buildTradePlan({
        direction,
        side,
        collateralAmount,
        quotedOutcomeAmount: BigInt(quote.outcomeAmount),
        slippageBps: 50
      });
      if (direction === "BUY") {
        setNotice("Approving tUSDG…");
        await session.write({ address: config.contracts.collateral, abi: erc20Abi, functionName: "approve", args: [market.fpmm, plan.approvalAmount] });
        setNotice("Submitting buy…");
        await session.write({ address: market.fpmm, abi: fpmmAbi, functionName: "buy", args: [collateralAmount, plan.outcomeIndex, plan.tradeLimit] });
      } else {
        setNotice("Approving outcome tokens…");
        await session.write({ address: config.contracts.conditionalTokens, abi: conditionalTokensAbi, functionName: "setApprovalForAll", args: [market.fpmm, true] });
        setNotice("Submitting sell…");
        await session.write({ address: market.fpmm, abi: fpmmAbi, functionName: "sell", args: [collateralAmount, plan.outcomeIndex, plan.tradeLimit] });
      }
      setNotice(`${direction === "BUY" ? "Buy" : "Sell"} confirmed on testnet.`);
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : "Trade failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="trade-panel">
      <div className="segmented">
        <button className={direction === "BUY" ? "active" : ""} onClick={() => setDirection("BUY")}>BUY</button>
        <button className={direction === "SELL" ? "active" : ""} onClick={() => setDirection("SELL")}>SELL</button>
      </div>
      <div className="side-toggle">
        <button className={side === "SHORT" ? "short active" : "short"} onClick={() => setSide("SHORT")}>SHORT</button>
        <button className={side === "LONG" ? "long active" : "long"} onClick={() => setSide("LONG")}>LONG</button>
      </div>
      <label className="amount-field">
        <span>{direction === "BUY" ? "SPEND" : "RECEIVE"}</span>
        <div><input value={amount} onChange={(event) => setAmount(event.target.value)} inputMode="decimal" /><b>tUSDG</b></div>
      </label>
      <div className="quote-box">
        <div><span>Estimated outcome</span><strong>{quote ? formatCollateral(quote.outcomeAmount) : "—"} {side}</strong></div>
        <div><span>Slippage limit</span><strong>0.50%</strong></div>
        <div><span>LP fee</span><strong>{(Number(market.fee) / 1e16).toFixed(1)}%</strong></div>
      </div>
      <button className={`trade-submit ${side.toLowerCase()}`} disabled={busy || market.resolved} onClick={() => void submit()}>
        {busy ? "Confirming…" : `Review ${direction.toLowerCase()}`} <ChevronRight size={17} />
      </button>
      <p className="fine-print">{wallet ? `Signing as ${shortenAddress(wallet.account)}` : "Connects your wallet on review."} Quotes expire with pool movement.</p>
    </aside>
  );
}

function LiquidityPanel({ market, wallet, ensureWallet, setNotice }: {
  market: Market;
  wallet: WalletSession | null;
  ensureWallet: () => Promise<WalletSession>;
  setNotice: (message: string | null) => void;
}) {
  const [amount, setAmount] = useState("1000");
  const [shares, setShares] = useState("0");
  const act = async (action: "add" | "remove" | "fees") => {
    try {
      const session = await ensureWallet();
      if (action === "add") {
        const value = parseUnits(amount, 6);
        setNotice("Approving liquidity collateral…");
        await session.write({ address: config.contracts.collateral, abi: erc20Abi, functionName: "approve", args: [market.fpmm, value] });
        await session.write({ address: market.fpmm, abi: fpmmAbi, functionName: "addFunding", args: [value, []] });
      } else if (action === "remove") {
        await session.write({ address: market.fpmm, abi: fpmmAbi, functionName: "removeFunding", args: [parseUnits(shares, 6)] });
      } else {
        await session.write({ address: market.fpmm, abi: fpmmAbi, functionName: "withdrawFees", args: [session.account] });
      }
      setNotice(`Liquidity ${action} transaction confirmed.`);
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : "Liquidity action failed");
    }
  };
  return (
    <section className="liquidity-box">
      <div><p className="eyebrow"><Droplets size={13} /> LIQUIDITY</p><h2>Make the market</h2></div>
      <p>LPs hold pool shares and earn the disclosed trading fee. Adding to an imbalanced pool may return excess outcome tokens.</p>
      <div className="liquidity-actions">
        <label><span>ADD tUSDG</span><input value={amount} onChange={(event) => setAmount(event.target.value)} /></label>
        <button onClick={() => void act("add")}>Add funding</button>
        <label><span>REMOVE LP</span><input value={shares} onChange={(event) => setShares(event.target.value)} /></label>
        <button onClick={() => void act("remove")}>Remove</button>
        <button className="claim-fees" onClick={() => void act("fees")}>Claim LP fees</button>
      </div>
      {!wallet && <small>Wallet connection required.</small>}
    </section>
  );
}

function PortfolioView({ markets, wallet, ensureWallet, setNotice }: {
  markets: Market[];
  wallet: WalletSession | null;
  ensureWallet: () => Promise<WalletSession>;
  setNotice: (message: string | null) => void;
}) {
  const [positions, setPositions] = useState<Position[]>([]);
  const [collateral, setCollateral] = useState<bigint>(0n);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (account: Address) => {
    if (!contractsConfigured()) return;
    setLoading(true);
    try {
      const balance = await publicClient.readContract({ address: config.contracts.collateral, abi: erc20Abi, functionName: "balanceOf", args: [account] });
      const loaded = await Promise.all(markets.map(async (market) => {
        const [shortId, longId] = await Promise.all([1n, 2n].map((indexSet) => publicClient.readContract({
          address: config.contracts.positionLens,
          abi: positionLensAbi,
          functionName: "positionId",
          args: [config.contracts.collateral, zeroBytes32, market.conditionId, indexSet]
        })));
        const [shortBalance, longBalance, lpBalance, fees] = await Promise.all([
          publicClient.readContract({ address: config.contracts.conditionalTokens, abi: conditionalTokensAbi, functionName: "balanceOf", args: [account, shortId] }),
          publicClient.readContract({ address: config.contracts.conditionalTokens, abi: conditionalTokensAbi, functionName: "balanceOf", args: [account, longId] }),
          publicClient.readContract({ address: market.fpmm, abi: fpmmAbi, functionName: "balanceOf", args: [account] }),
          publicClient.readContract({ address: market.fpmm, abi: fpmmAbi, functionName: "feesWithdrawableBy", args: [account] })
        ]);
        return { market, shortBalance, longBalance, lpBalance, fees };
      }));
      setCollateral(balance);
      setPositions(loaded.filter((position) => position.shortBalance + position.longBalance + position.lpBalance > 0n));
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : "Portfolio read failed");
    } finally {
      setLoading(false);
    }
  }, [markets, setNotice]);

  useEffect(() => { if (wallet) void load(wallet.account); }, [wallet, load]);

  const redeem = async (market: Market, indexSet: 1n | 2n) => {
    try {
      const session = await ensureWallet();
      await session.write({
        address: config.contracts.conditionalTokens,
        abi: conditionalTokensAbi,
        functionName: "redeemPositions",
        args: [config.contracts.collateral, zeroBytes32, market.conditionId, [indexSet]]
      });
      setNotice("Redemption confirmed.");
      void load(session.account);
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : "Redemption failed");
    }
  };

  return (
    <section className="page-section portfolio-page">
      <p className="eyebrow">WALLET LEDGER</p>
      <h1>Your exposure</h1>
      <p className="page-deck">Outcome tokens, LP shares, withdrawable fees, and redeemable positions—read directly from Robinhood Chain testnet.</p>
      {!wallet ? (
        <div className="connect-panel"><Wallet size={34} /><h2>Connect to inspect positions</h2><p>No address history is sent to a backend.</p><button className="signal-button" onClick={() => void ensureWallet()}>Connect wallet</button></div>
      ) : (
        <>
          <div className="portfolio-summary"><span>AVAILABLE tUSDG</span><strong>${formatCollateral(collateral)}</strong><button onClick={() => void load(wallet.account)}><RefreshCw size={15} /> Refresh</button></div>
          {loading && <p>Reading positions…</p>}
          {!loading && !positions.length && <div className="empty-state"><h3>No current exposure.</h3><p>Buy an outcome or seed a pool to populate this ledger.</p></div>}
          <div className="position-list">
            {positions.map((position) => (
              <article key={position.market.id} className="position-row">
                <div><span>MARKET #{position.market.id}</span><h3>{marketTitle(position.market)}</h3></div>
                <div><span>SHORT</span><strong>{formatCollateral(position.shortBalance)}</strong></div>
                <div><span>LONG</span><strong>{formatCollateral(position.longBalance)}</strong></div>
                <div><span>LP / FEES</span><strong>{formatCollateral(position.lpBalance)} / ${formatCollateral(position.fees)}</strong></div>
                <div className="redeem-actions">
                  <button disabled={!position.market.resolved || position.shortBalance === 0n} onClick={() => void redeem(position.market, 1n)}>Redeem SHORT</button>
                  <button disabled={!position.market.resolved || position.longBalance === 0n} onClick={() => void redeem(position.market, 2n)}>Redeem LONG</button>
                </div>
              </article>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function CreateView({ wallet, ensureWallet, setNotice, onCreated }: {
  wallet: WalletSession | null;
  ensureWallet: () => Promise<WalletSession>;
  setNotice: (message: string | null) => void;
  onCreated: () => void;
}) {
  const defaultResolution = useMemo(() => toLocalDatetime(Date.now() + 24 * 60 * 60 * 1_000), []);
  const [metric, setMetric] = useState<"funding" | "volatility">("funding");
  const [coin, setCoin] = useState("BTC");
  const [lower, setLower] = useState("-0.01");
  const [upper, setUpper] = useState("0.03");
  const [resolution, setResolution] = useState(defaultResolution);
  const [liquidity, setLiquidity] = useState("10000");
  const [busy, setBusy] = useState(false);

  const changeMetric = (next: "funding" | "volatility") => {
    setMetric(next);
    if (next === "volatility") { setLower("20"); setUpper("120"); }
    else { setLower("-0.01"); setUpper("0.03"); }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      setBusy(true);
      const session = await ensureWallet();
      const resolutionMs = new Date(resolution).getTime();
      if (!Number.isFinite(resolutionMs) || resolutionMs < Date.now() + 15 * 60 * 1_000) throw new Error("Resolution must be at least 15 minutes away");
      const lowerBound = percentToScalar(lower);
      const upperBound = percentToScalar(upper);
      if (lowerBound >= upperBound) throw new Error("Upper bound must exceed lower bound");
      const spec = {
        version: 1 as const,
        source: "hyperliquid" as const,
        metric,
        coin: coin.toUpperCase(),
        startTime: Date.now(),
        endTime: resolutionMs,
        interval: "1h" as const,
        aggregation: metric === "funding" ? "arithmetic-mean" as const : "root-mean-square-log-returns" as const,
        scale: "1000000000000" as const
      };
      setNotice("Publishing immutable oracle specification…");
      const metadata = await storeMetadata(spec);
      const initialLiquidity = parseUnits(liquidity, 6);
      setNotice("Approving initial liquidity…");
      await session.write({ address: config.contracts.collateral, abi: erc20Abi, functionName: "approve", args: [config.contracts.marketFactory, initialLiquidity] });
      setNotice("Creating and seeding market atomically…");
      await session.write({
        address: config.contracts.marketFactory,
        abi: marketFactoryAbi,
        functionName: "createMarket",
        args: [{
          marketKey: keccak256(toBytes(`HYPERLIQUID:${coin.toUpperCase()}:1H:${metric.toUpperCase()}`)),
          metric: metric === "funding" ? 0 : 1,
          lowerBound,
          upperBound,
          resolutionTime: BigInt(Math.floor(resolutionMs / 1_000)),
          reportDeadline: BigInt(Math.floor(resolutionMs / 1_000) + 48 * 60 * 60),
          fee: 10_000_000_000_000_000n,
          initialLiquidity,
          distributionHint: [1n, 1n],
          descriptorHash: metadata.descriptorHash,
          metadataURI: metadata.metadataURI
        }]
      });
      setNotice("Market created and liquidity seeded.");
      onCreated();
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : "Market creation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="page-section create-page">
      <p className="eyebrow">PERMISSIONLESS FACTORY</p>
      <h1>Define a market</h1>
      <p className="page-deck">Bind the data window, payout range, deadline, fee, and initial liquidity in one atomic transaction.</p>
      <form className="create-grid" onSubmit={(event) => void submit(event)}>
        <div className="form-main">
          <fieldset><legend>01 / INSTRUMENT</legend><div className="choice-grid"><button type="button" className={metric === "funding" ? "choice active" : "choice"} onClick={() => changeMetric("funding")}><Activity /><strong>Funding rate</strong><span>Arithmetic mean of hourly payments</span></button><button type="button" className={metric === "volatility" ? "choice active" : "choice"} onClick={() => changeMetric("volatility")}><Gauge /><strong>Realized volatility</strong><span>Annualized RMS log returns</span></button></div></fieldset>
          <fieldset><legend>02 / ORACLE WINDOW</legend><div className="field-row"><label>Hyperliquid coin<input value={coin} onChange={(event) => setCoin(event.target.value.toUpperCase())} pattern="[A-Z0-9]{1,12}" required /></label><label>Resolution time<input type="datetime-local" value={resolution} onChange={(event) => setResolution(event.target.value)} required /></label></div><p className="field-note">Source: Hyperliquid public Info API · interval: 1h · report grace: 48h</p></fieldset>
          <fieldset><legend>03 / PAYOUT RANGE</legend><div className="field-row"><label htmlFor="lower-bound">Lower bound (%)<input id="lower-bound" aria-label="Lower bound" value={lower} onChange={(event) => setLower(event.target.value)} inputMode="decimal" required /></label><label htmlFor="upper-bound">Upper bound (%)<input id="upper-bound" aria-label="Upper bound" value={upper} onChange={(event) => setUpper(event.target.value)} inputMode="decimal" required /></label></div><div className="payout-explainer"><span>≤ lower: SHORT pays 1</span><span>inside: linear split</span><span>≥ upper: LONG pays 1</span></div></fieldset>
          <fieldset><legend>04 / LIQUIDITY</legend><label>Initial tUSDG<input value={liquidity} onChange={(event) => setLiquidity(event.target.value)} inputMode="decimal" required /></label><p className="field-note">Minimum 100 tUSDG · equal SHORT/LONG seed · fixed 1% LP fee</p></fieldset>
        </div>
        <aside className="create-summary"><p className="eyebrow">PRE-FLIGHT</p><h2>{coin || "—"} {metric === "funding" ? "funding" : "realized vol"}</h2><div><span>Range</span><strong>{lower}% → {upper}%</strong></div><div><span>Resolve</span><strong>{resolution ? new Date(resolution).toLocaleString() : "—"}</strong></div><div><span>Liquidity</span><strong>${liquidity || "0"} tUSDG</strong></div><div><span>Operator fee</span><strong>0%</strong></div><ul><li>Testnet only</li><li>Metadata content-addressed</li><li>LP shares sent to creator</li><li>Timeout equal refund</li></ul><button type="submit" className="signal-button" disabled={busy}>{busy ? "Creating…" : wallet ? "Create + seed" : "Connect + create"}<ChevronRight size={17} /></button></aside>
      </form>
    </section>
  );
}

function DocsView() {
  const agentUrl = `${config.apiUrl}/.well-known/agent.json`;
  return (
    <section className="page-section docs-page">
      <p className="eyebrow"><Bot size={14} /> HUMAN + AGENT ACCESS</p>
      <h1>Agent interface</h1>
      <p className="page-deck">Every read and write path is public, deterministic, and chain-bound. Agents use the same pools as the interface.</p>
      <div className="docs-grid">
        <article><span>01</span><h2>Discover</h2><code>GET /v1/markets</code><p>Registry state, scalar bounds, deadlines, pool addresses, and indicative quotes.</p></article>
        <article><span>02</span><h2>Price</h2><code>GET /v1/quote</code><p>Exact FPMM buy output or sell token requirement at the current block.</p></article>
        <article><span>03</span><h2>Observe</h2><code>GET /v1/feeds/funding</code><code>GET /v1/feeds/volatility</code><p>Reproducible Hyperliquid windows with evidence hashes and 1e12 scaling.</p></article>
        <article><span>04</span><h2>Settle</h2><code>GET /v1/report-payload</code><p>EIP-712 payload bound to question, scalar value, evidence, nonce, oracle, and chain.</p></article>
      </div>
      <a className="agent-link" href={agentUrl} target="_blank" rel="noreferrer"><Bot /> Open machine-readable contract <ExternalLink size={16} /></a>
      <div className="trust-grid"><div><ShieldCheck /><h3>Fully collateralized</h3><p>Complete outcome sets are minted only against tUSDG held by Conditional Tokens.</p></div><div><Radio /><h3>Bounded oracle</h3><p>Signed reports resolve within the declared range; missed deadlines unlock equal refunds.</p></div><div><Droplets /><h3>LP-owned fees</h3><p>The 1% pool fee accrues to fungible FPMM liquidity shares. No protocol token exists.</p></div></div>
    </section>
  );
}

function marketTitle(market: Market): string {
  return `${market.metric === 0 ? "Funding rate" : "Realized volatility"} · Market #${market.id}`;
}

function safeParseCollateral(value: string): bigint {
  try { return parseUnits(value || "0", 6); } catch { return 0n; }
}

function toLocalDatetime(timestamp: number): string {
  const date = new Date(timestamp - new Date(timestamp).getTimezoneOffset() * 60_000);
  return date.toISOString().slice(0, 16);
}
